import { useEffect, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { BbotButton, BbotModal, PageLoadingSpinner, notification } from "top-component-library";
import { fulfillmentMethodsToIds, generalErrorAlert, removePatronChoice } from "util/Utils";
import {
  buildAlwaysOpenSchedule,
  buildInitialOrderRateLimit,
  buildSavePayload,
  buildValidatedSchedule,
  convertSubtotalToCents,
  validateOrderRateLimit,
  validateSubtotalMinMax,
} from "./orderRateLimitUtils";
import OrderRateLimitNameStep from "./sections/OrderRateLimitNameStep";
import OrderRateLimitTypeStep from "./sections/OrderRateLimitTypeStep";
import OrderRateLimitRateLimitStep from "./sections/OrderRateLimitRateLimitStep";
import OrderRateLimitTagMinMaxStep from "./sections/OrderRateLimitTagMinMaxStep";
import OrderRateLimitSubtotalStep from "./sections/OrderRateLimitSubtotalStep";
import OrderRateLimitPrereqStep from "./sections/OrderRateLimitPrereqStep";
import OrderRateLimitTagLimitsStep from "./sections/OrderRateLimitTagLimitsStep";
import OrderRateLimitConditionsStep from "./sections/OrderRateLimitConditionsStep";
import OrderRateLimitScheduleStep from "./sections/OrderRateLimitScheduleStep";

const SINGLE_STEP_AFTER_TYPE_CHOICE = ["inputRateLimit", "inputMinMax", "inputPrereq", "inputTagLimits"];

// Mirrors addOrderRateLimitController's saveConfigStep(): a linear wizard with one branch point
// (chooseThrottleType fans out to one of five type-specific steps), then a shared
// conditions -> schedule tail. There's no "back" step in legacy either, only Cancel.
const OrderRateLimitDialog = ({ visible, config, customerId, availableThrottleSettings, onClose, onSave }) => {
  const [step, setStep] = useState("inputName");
  const [orderRateLimit, setOrderRateLimit] = useState(() => buildInitialOrderRateLimit(config).orderRateLimit);
  const [showActiveSchedule, setShowActiveSchedule] = useState(false);
  const [showPrereqMinutes, setShowPrereqMinutes] = useState(false);
  const [locationsData, setLocationsData] = useState(null);
  const [loadingConditions, setLoadingConditions] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!visible) return;
    const initial = buildInitialOrderRateLimit(config);
    setOrderRateLimit(initial.orderRateLimit);
    setShowActiveSchedule(initial.showActiveSchedule);
    setShowPrereqMinutes(Boolean(initial.orderRateLimit.prereq_tag_minutes_in_past));
    setStep("inputName");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    const load = async () => {
      setLoadingConditions(true);
      try {
        const res = await axios.get("api/ownerlocations", { params: { customer_id: customerId } });
        if (cancelled) return;
        setLocationsData({ locations: res.data.locations, fulfillmentMethods: res.data.fulfillment_methods });
      } catch (error) {
        if (!cancelled) generalErrorAlert(error, "Trouble loading locations. Please try again.", true);
      } finally {
        if (!cancelled) setLoadingConditions(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [visible, customerId]);

  // Mirrors addOrderRateLimitController's getLocations(): a saved 'all' sentinel is expanded
  // back into the real, current list of fulfillment method keys once that list is known.
  useEffect(() => {
    if (!locationsData) return;
    setOrderRateLimit((current) => {
      if (current.fulfillment_methods.length === 1 && current.fulfillment_methods[0] === "all") {
        return {
          ...current,
          fulfillment_methods: fulfillmentMethodsToIds(removePatronChoice(locationsData.fulfillmentMethods)),
        };
      }
      return current;
    });
  }, [locationsData]);

  const update = (changes) => setOrderRateLimit((current) => ({ ...current, ...changes }));

  const isStepValid = () => {
    switch (step) {
      case "inputName":
        return Boolean(orderRateLimit.name_for_owner);
      case "chooseThrottleType":
        return Boolean(orderRateLimit.effect_type);
      case "inputRateLimit":
        return (
          Number(orderRateLimit.max_orders_per_desired_time) >= 1 &&
          Number(orderRateLimit.max_orders_per_desired_time) <= 10000
        );
      case "inputMinMax":
        return Boolean(orderRateLimit.checkout_blocking_tag);
      case "inputSubtotalMinMax":
        return Boolean(orderRateLimit.items_pretax_min) || Boolean(orderRateLimit.items_pretax_max);
      case "inputPrereq":
        return Boolean(orderRateLimit.prereq_tag);
      default:
        return true;
    }
  };

  const handleSave = async () => {
    let schedule;
    let { start_date: startDate, end_date: endDate } = orderRateLimit;
    if (!showActiveSchedule) {
      schedule = buildAlwaysOpenSchedule();
      startDate = null;
      endDate = null;
    } else {
      const result = buildValidatedSchedule(orderRateLimit.active_schedule);
      if (!result.isValid) {
        notification.error({ message: "Schedule formatting is invalid" });
        return;
      }
      schedule = result.schedule;
    }

    const candidate = { ...orderRateLimit, active_schedule: schedule, start_date: startDate, end_date: endDate };
    const error = validateOrderRateLimit(candidate);
    if (error) {
      notification.error({ message: error });
      return;
    }

    const allFulfillmentMethodKeys = fulfillmentMethodsToIds(removePatronChoice(locationsData?.fulfillmentMethods));
    const payload = buildSavePayload(candidate, schedule, startDate, endDate, allFulfillmentMethodKeys);
    setSaving(true);
    const success = await onSave(payload);
    setSaving(false);
    if (success) onClose();
  };

  const advance = () => {
    if (step === "inputName") {
      setStep("chooseThrottleType");
    } else if (step === "chooseThrottleType") {
      if (orderRateLimit.effect_type === "tag_prereq") {
        update({
          prereq_tag: orderRateLimit.prereq_tag || "item_class_food",
          unlocked_tag: orderRateLimit.unlocked_tag || "alcohol",
          unlocked_tag_max_qty: orderRateLimit.unlocked_tag_max_qty ?? 0,
        });
        setStep("inputPrereq");
      } else if (orderRateLimit.effect_type === "order_rate_limit") {
        setStep("inputRateLimit");
      } else if (orderRateLimit.effect_type === "tag_minmax") {
        setStep("inputMinMax");
      } else if (orderRateLimit.effect_type === "items_pretax_minmax") {
        setStep("inputSubtotalMinMax");
      } else if (orderRateLimit.effect_type === "tag_rate_limit") {
        setStep("inputTagLimits");
      }
    } else if (SINGLE_STEP_AFTER_TYPE_CHOICE.includes(step)) {
      setStep("inputThrottleConditions");
    } else if (step === "inputSubtotalMinMax") {
      const error = validateSubtotalMinMax(orderRateLimit.items_pretax_min, orderRateLimit.items_pretax_max);
      if (error) {
        notification.error({ message: error });
        return;
      }
      update(convertSubtotalToCents(orderRateLimit.items_pretax_min, orderRateLimit.items_pretax_max));
      setStep("inputThrottleConditions");
    } else if (step === "inputThrottleConditions") {
      setStep("inputSchedule");
    } else {
      handleSave();
    }
  };

  const renderStep = () => {
    switch (step) {
      case "inputName":
        return (
          <OrderRateLimitNameStep
            name={orderRateLimit.name_for_owner}
            onChange={(value) => update({ name_for_owner: value })}
          />
        );
      case "chooseThrottleType":
        return (
          <OrderRateLimitTypeStep
            effectType={orderRateLimit.effect_type}
            onChange={(value) => update({ effect_type: value })}
          />
        );
      case "inputRateLimit":
        return (
          <OrderRateLimitRateLimitStep
            maxOrdersPerDesiredTime={orderRateLimit.max_orders_per_desired_time}
            onChange={(value) => update({ max_orders_per_desired_time: value })}
          />
        );
      case "inputMinMax":
        return (
          <OrderRateLimitTagMinMaxStep
            availableThrottleSettings={availableThrottleSettings}
            checkoutBlockingTag={orderRateLimit.checkout_blocking_tag}
            minQty={orderRateLimit.checkout_blocking_tag_min_qty}
            maxQty={orderRateLimit.checkout_blocking_tag_max_qty}
            messageIfViolated={orderRateLimit.message_if_violated}
            onChangeTag={(value) => update({ checkout_blocking_tag: value })}
            onChangeMin={(value) => update({ checkout_blocking_tag_min_qty: value })}
            onChangeMax={(value) => update({ checkout_blocking_tag_max_qty: value })}
            onChangeMessage={(value) => update({ message_if_violated: value })}
          />
        );
      case "inputSubtotalMinMax":
        return (
          <OrderRateLimitSubtotalStep
            min={orderRateLimit.items_pretax_min}
            max={orderRateLimit.items_pretax_max}
            messageIfViolated={orderRateLimit.message_if_violated}
            onChangeMin={(value) => update({ items_pretax_min: value })}
            onChangeMax={(value) => update({ items_pretax_max: value })}
            onChangeMessage={(value) => update({ message_if_violated: value })}
          />
        );
      case "inputPrereq":
        return (
          <OrderRateLimitPrereqStep
            availableThrottleSettings={availableThrottleSettings}
            unlockedTag={orderRateLimit.unlocked_tag}
            prereqTag={orderRateLimit.prereq_tag}
            showPrereqMinutes={showPrereqMinutes}
            prereqTagMinutesInPast={orderRateLimit.prereq_tag_minutes_in_past}
            prereqTagMinQty={orderRateLimit.prereq_tag_min_qty}
            unlockedTagMaxQty={orderRateLimit.unlocked_tag_max_qty}
            messageIfViolated={orderRateLimit.message_if_violated}
            onChangeUnlockedTag={(value) => update({ unlocked_tag: value })}
            onChangePrereqTag={(value) => update({ prereq_tag: value })}
            onChangeShowPrereqMinutes={(value) => {
              setShowPrereqMinutes(value);
              if (!value) update({ prereq_tag_minutes_in_past: 0 });
            }}
            onChangePrereqTagMinutesInPast={(value) => update({ prereq_tag_minutes_in_past: value })}
            onChangePrereqTagMinQty={(value) => update({ prereq_tag_min_qty: value })}
            onChangeUnlockedTagMaxQty={(value) => update({ unlocked_tag_max_qty: value })}
            onChangeMessage={(value) => update({ message_if_violated: value })}
          />
        );
      case "inputTagLimits":
        return (
          <OrderRateLimitTagLimitsStep
            availableThrottleSettings={availableThrottleSettings}
            rateLimitedTag={orderRateLimit.rate_limited_tag}
            maxPerDesiredTime={orderRateLimit.max_rate_limited_tag_per_desired_time}
            maxPerDay={orderRateLimit.max_rate_limited_tag_per_day}
            maxBetweenStartAndEnd={orderRateLimit.max_rate_limited_tag_between_start_and_end}
            onChangeTag={(value) => update({ rate_limited_tag: value })}
            onChangeMaxPerDesiredTime={(value) => update({ max_rate_limited_tag_per_desired_time: value })}
            onChangeMaxPerDay={(value) => update({ max_rate_limited_tag_per_day: value })}
            onChangeMaxBetweenStartAndEnd={(value) => update({ max_rate_limited_tag_between_start_and_end: value })}
          />
        );
      case "inputThrottleConditions":
        return loadingConditions || !locationsData ? (
          <PageLoadingSpinner />
        ) : (
          <OrderRateLimitConditionsStep
            locations={locationsData.locations}
            fulfillmentMethods={locationsData.fulfillmentMethods}
            fulfillmentMethodKeys={orderRateLimit.fulfillment_methods}
            locationIds={orderRateLimit.location_ids}
            onChange={({ fulfillment_methods, location_ids }) => update({ fulfillment_methods, location_ids })}
          />
        );
      case "inputSchedule":
        return (
          <OrderRateLimitScheduleStep
            showActiveSchedule={showActiveSchedule}
            startDate={orderRateLimit.start_date}
            endDate={orderRateLimit.end_date}
            activeSchedule={orderRateLimit.active_schedule}
            onChangeShowActiveSchedule={setShowActiveSchedule}
            onChangeStartDate={(value) => update({ start_date: value })}
            onChangeEndDate={(value) => update({ end_date: value })}
            onChangeDay={(day, value) =>
              update({
                active_schedule: {
                  ...orderRateLimit.active_schedule,
                  [day]: { ...orderRateLimit.active_schedule[day], customer_input: value },
                },
              })
            }
          />
        );
      default:
        return null;
    }
  };

  return (
    <BbotModal
      title={`${config ? "Edit" : "Add"} Checkout Limitation`}
      visible={visible}
      onCancel={onClose}
      width={720}
      bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      footer={[
        <BbotButton key="cancel" onClick={onClose}>
          Cancel
        </BbotButton>,
        <BbotButton key="next" type="primary" loading={saving} disabled={!isStepValid()} onClick={advance}>
          {step === "inputSchedule" ? "Save" : "Next"}
        </BbotButton>,
      ]}
    >
      {renderStep()}
    </BbotModal>
  );
};

OrderRateLimitDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  config: PropTypes.shape({ id: PropTypes.string }),
  customerId: PropTypes.string.isRequired,
  availableThrottleSettings: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string.isRequired, display_name: PropTypes.string.isRequired })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

OrderRateLimitDialog.defaultProps = {
  config: null,
};

export default OrderRateLimitDialog;
